import React, { useState, useEffect } from 'react';

const statusColors = {
  new: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  contacted: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  booked: 'bg-gold-500/20 text-gold-400 border-gold-500/30',
  completed: 'bg-charcoal-500/20 text-charcoal-400 border-charcoal-500/30',
};

export default function Admin() {
  const [consultations, setConsultations] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (token) setAuthenticated(true);
  }, []);

  useEffect(() => {
    if (authenticated) {
      loadConsultations();
    }
  }, [authenticated]);

  const authHeader = () => ({
    Authorization: `Bearer ${sessionStorage.getItem('admin_token')}`,
    'Content-Type': 'application/json',
  });

  const loadConsultations = async () => {
    const res = await fetch('/api/consultations', { headers: authHeader() });
    if (res.status === 401) { sessionStorage.removeItem('admin_token'); setAuthenticated(false); return; }
    const data = await res.json();
    setConsultations(data);
  };

  const updateStatus = async (id, newStatus) => {
    setConsultations((prev) => prev.map((c) => c.id === id ? { ...c, status: newStatus } : c));
    await fetch(`/api/consultations/${id}`, {
      method: 'PATCH',
      headers: authHeader(),
      body: JSON.stringify({ status: newStatus }),
    });
  };

  const deleteConsultation = async (id) => {
    if (!window.confirm('Delete this consultation request?')) return;
    setConsultations((prev) => prev.filter((c) => c.id !== id));
    await fetch(`/api/consultations/${id}`, { method: 'DELETE', headers: authHeader() });
  };

  const updateNotes = async (id, notes) => {
    setConsultations((prev) => prev.map((c) => c.id === id ? { ...c, notes } : c));
    await fetch(`/api/consultations/${id}`, {
      method: 'PATCH',
      headers: authHeader(),
      body: JSON.stringify({ notes }),
    });
  };

  const filtered = consultations
    .filter((c) => filter === 'all' || c.status === filter)
    .filter((c) => {
      if (!search.trim()) return true;
      const q = search.toLowerCase();
      return c.name.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
    });

  const exportCSV = () => {
    const headers = ['ID', 'Name', 'Email', 'Phone', 'Service', 'Message', 'Status', 'Notes', 'Submitted At'];
    const rows = filtered.map((c) => [
      c.id,
      c.name,
      c.email,
      c.phone || '',
      c.service || '',
      `"${(c.message || '').replace(/"/g, '""')}"`,
      c.status,
      `"${(c.notes || '').replace(/"/g, '""')}"`,
      new Date(c.submitted_at).toLocaleString('en-GB'),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `consultations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const stats = {
    total: consultations.length,
    new: consultations.filter((c) => c.status === 'new').length,
    contacted: consultations.filter((c) => c.status === 'contacted').length,
    booked: consultations.filter((c) => c.status === 'booked').length,
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Login failed');
      sessionStorage.setItem('admin_token', data.token);
      setAuthenticated(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-charcoal-950 flex items-center justify-center px-6">
        <form
          onSubmit={handleLogin}
          className="bg-charcoal-900 rounded-2xl p-8 w-full max-w-sm shadow-2xl border border-charcoal-800"
        >
          <div className="text-center mb-8">
            <h1 className="font-serif text-2xl text-white mb-2">Admin Panel</h1>
            <p className="text-sm text-charcoal-400">
              Dr. Noora Ibrahim Medical Aesthetics
            </p>
          </div>
          <label className="block text-xs tracking-wider uppercase text-charcoal-500 mb-2 font-medium">
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-3 rounded-lg border border-charcoal-700 bg-charcoal-800 text-sm text-white focus:outline-none focus:border-gold-400 focus:ring-1 focus:ring-gold-400 transition-all mb-6"
            placeholder="Enter admin password"
            autoFocus
          />
          {error && (
            <p className="text-red-400 text-xs mb-4">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-lg bg-gold-500 text-charcoal-950 font-medium text-sm tracking-wider uppercase hover:bg-gold-400 transition-colors disabled:opacity-50"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-charcoal-950">
      {/* Header */}
      <header className="bg-charcoal-900 border-b border-charcoal-800">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="font-serif text-xl text-white">Admin Panel</h1>
            <p className="text-xs text-charcoal-400 mt-0.5">
              Consultation Requests
            </p>
          </div>
          <div className="flex items-center gap-4">
            <a
              href="/"
              className="text-xs text-charcoal-400 hover:text-white transition-colors"
            >
              &larr; Back to Site
            </a>
            <button
              onClick={exportCSV}
              className="text-xs px-3 py-1.5 rounded-lg border border-gold-500/40 text-gold-400 hover:bg-gold-500/10 transition-colors"
            >
              Export CSV
            </button>
            <button
              onClick={() => { sessionStorage.removeItem('admin_token'); setAuthenticated(false); }}
              className="text-xs text-red-400 hover:text-red-300 transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total', value: stats.total, color: 'text-white' },
            { label: 'New', value: stats.new, color: 'text-emerald-400' },
            { label: 'Contacted', value: stats.contacted, color: 'text-blue-400' },
            { label: 'Booked', value: stats.booked, color: 'text-gold-400' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="bg-charcoal-900 rounded-xl p-5 border border-charcoal-800"
            >
              <p className="text-xs text-charcoal-500 uppercase tracking-wider mb-1">
                {stat.label}
              </p>
              <p className={`text-2xl font-serif font-medium ${stat.color}`}>
                {stat.value}
              </p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="w-full md:w-80 px-4 py-2.5 pl-9 rounded-lg border border-charcoal-700 bg-charcoal-900 text-sm text-white placeholder-charcoal-500 focus:outline-none focus:border-gold-500/50"
          />
          <svg className="absolute left-3 top-3 w-4 h-4 text-charcoal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
          </svg>
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-2.5 text-charcoal-500 hover:text-white text-xs">✕</button>
          )}
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {['all', 'new', 'contacted', 'booked', 'completed'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg text-xs uppercase tracking-wider transition-colors ${
                filter === f
                  ? 'bg-gold-500 text-charcoal-950 font-medium'
                  : 'bg-charcoal-900 text-charcoal-400 hover:text-white border border-charcoal-800'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="bg-charcoal-900 rounded-xl border border-charcoal-800 p-16 text-center">
            <p className="text-charcoal-500 text-sm">
              {search ? `No results for "${search}"` : 'No consultation requests yet.'}
            </p>
          </div>
        ) : (
          <div className="bg-charcoal-900 rounded-xl border border-charcoal-800 overflow-hidden">
            {/* Desktop table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-charcoal-800">
                    <th className="text-left text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Name
                    </th>
                    <th className="text-left text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Contact
                    </th>
                    <th className="text-left text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Treatment
                    </th>
                    <th className="text-left text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Message
                    </th>
                    <th className="text-left text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Date
                    </th>
                    <th className="text-left text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Status
                    </th>
                    <th className="text-right text-[10px] tracking-wider uppercase text-charcoal-500 font-medium px-6 py-4">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <React.Fragment key={c.id}>
                      <tr className="border-b border-charcoal-800/20 hover:bg-charcoal-800/30 transition-colors">
                        <td className="px-6 py-4">
                          <p className="text-sm text-white font-medium">
                            {c.name}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-sm text-charcoal-300">{c.email}</p>
                          <p className="text-xs text-charcoal-500">{c.phone}</p>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-xs text-charcoal-300 capitalize">
                            {c.service || '—'}
                          </span>
                        </td>
                        <td className="px-6 py-4 max-w-xs">
                          <p className="text-xs text-charcoal-400 truncate">
                            {c.message || '—'}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <p className="text-xs text-charcoal-400">
                            {new Date(c.submitted_at).toLocaleDateString('en-GB', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric',
                            })}
                          </p>
                          <p className="text-[10px] text-charcoal-600">
                            {new Date(c.submitted_at).toLocaleTimeString('en-GB', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={c.status}
                            onChange={(e) => updateStatus(c.id, e.target.value)}
                            className={`text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border bg-transparent cursor-pointer focus:outline-none ${
                              statusColors[c.status] || statusColors.new
                            }`}
                          >
                            <option value="new">New</option>
                            <option value="contacted">Contacted</option>
                            <option value="booked">Booked</option>
                            <option value="completed">Completed</option>
                          </select>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => deleteConsultation(c.id)}
                            className="text-xs text-red-500/60 hover:text-red-400 transition-colors"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                      <tr className="border-b border-charcoal-800/50">
                        <td colSpan={7} className="px-6 pb-3">
                          <textarea
                            defaultValue={c.notes || ''}
                            onBlur={(e) => updateNotes(c.id, e.target.value)}
                            rows={2}
                            placeholder="Private notes..."
                            className="w-full bg-charcoal-800/50 border border-charcoal-700/50 rounded-lg px-3 py-2 text-xs text-charcoal-300 placeholder-charcoal-600 focus:outline-none focus:border-gold-500/40 resize-none"
                          />
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-charcoal-800/50">
              {filtered.map((c) => (
                <div key={c.id} className="p-5 space-y-3">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-white font-medium">{c.name}</p>
                      <p className="text-xs text-charcoal-400">{c.email}</p>
                      {c.phone && (
                        <p className="text-xs text-charcoal-500">{c.phone}</p>
                      )}
                    </div>
                    <select
                      value={c.status}
                      onChange={(e) => updateStatus(c.id, e.target.value)}
                      className={`text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-full border bg-transparent cursor-pointer focus:outline-none ${
                        statusColors[c.status] || statusColors.new
                      }`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="booked">Booked</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                  {c.service && (
                    <p className="text-xs text-charcoal-300 capitalize">
                      Treatment: {c.service}
                    </p>
                  )}
                  {c.message && (
                    <p className="text-xs text-charcoal-400">{c.message}</p>
                  )}
                  <textarea
                    defaultValue={c.notes || ''}
                    onBlur={(e) => updateNotes(c.id, e.target.value)}
                    rows={2}
                    placeholder="Private notes..."
                    className="w-full bg-charcoal-800/50 border border-charcoal-700/50 rounded-lg px-3 py-2 text-xs text-charcoal-300 placeholder-charcoal-600 focus:outline-none focus:border-gold-500/40 resize-none"
                  />
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] text-charcoal-600">
                      {new Date(c.submitted_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                    <button
                      onClick={() => deleteConsultation(c.id)}
                      className="text-xs text-red-500/60 hover:text-red-400 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
